import { useState, useEffect, useCallback } from "react";
import * as XLSX from "xlsx";
import {
	CiSearch,
	CiRedo,
	CiCircleCheck,
	CiCircleRemove,
	CiCirclePlus,
	CiGrid41,
	CiSettings,
	CiCircleList,
	CiStar,
	CiUser,
	CiExport,
	CiTrophy,
	CiCalendar,
	CiFilter,
} from "react-icons/ci";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
	ArcElement,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import PageTitle from "../../components/common/PageTitle";
import RSTabs from "../../components/ui/RSTabs";
import FullPageLoader from "../../components/ui/FullPageLoader";
import ButtonTextIcon from "../../components/ui/ButtonTextIcon";
import ButtonLightTextIcon from "../../components/ui/ButtonLightTextIcon";
import PopUpModal from "../../components/common/PopUpModal";
import { API_URL } from "../../utilities/constants";
import { useUI } from "../../context/ui";
import {
	formatDateTime,
	formatAmountValue,
	decodeRolesFromToken,
} from "../../utilities/helpers";
import { useOutsideClick } from "../../utilities/hooks";

ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
	ArcElement
);

// ====== Helpers ====== //

const buildQueryParams = (params) =>
	Object.entries(params)
		.filter(([_, value]) => value !== "" && value !== null && value !== undefined)
		.map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
		.join("&");

const getToday = () => new Date().toISOString().split("T")[0];
const getMonthStart = () => {
	const d = new Date();
	d.setDate(1);
	return d.toISOString().split("T")[0];
};
const formatYYYYMM = (yyyymm) => {
	if (!yyyymm || yyyymm.length !== 6) return yyyymm;
	const y = yyyymm.substring(0, 4);
	const m = yyyymm.substring(4, 6);
	const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
	return `${months[parseInt(m) - 1]} ${y}`;
};

// Format a Date for `datetime-local` input (local timezone, no seconds)
const formatForDatetimeLocal = (date = new Date()) => {
	const tzOffset = date.getTimezoneOffset() * 60000;
	const localISO = new Date(date.getTime() - tzOffset).toISOString();
	return localISO.slice(0, 16);
};
const StatusBadge = ({ status }) => {
	const colorMap = {
		CAPTURED: "#f59e0b",
		FIRST_TXN_DONE: "#3b82f6",
		REWARDED: "#10b981",
		NONE: "#9ca3af",
		PENDING: "#f59e0b",
		CREDITED: "#10b981",
		FAILED: "#ef4444",
		REVERSED: "#6b7280",
		APPROVED: "#10b981",
		PAID: "#2980B9",
		DRAFT: "#9ca3af",
		ACTIVE: "#10b981",
		COMPLETED: "#6b7280",
		INACTIVE: "#ef4444",
		REJECTED: "#ef4444",
	};
	const bg = colorMap[status] || "#9ca3af";
	return (
		<span
			style={{
				display: "inline-block",
				padding: "3px 10px",
				borderRadius: "12px",
				fontSize: "11px",
				fontWeight: 600,
				color: "white",
				backgroundColor: bg,
				textTransform: "uppercase",
				whiteSpace: "nowrap",
			}}>
			{status}
		</span>
	);
};

const SimplePagination = ({ page, totalPages, onPageChange }) => {
	if (totalPages <= 1) return null;
	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: "8px",
				padding: "16px 20px",
				justifyContent: "flex-end",
			}}>
			<button
				className='light'
				style={{ height: "34px", padding: "0 12px", fontSize: "13px" }}
				disabled={page === 0}
				onClick={() => onPageChange(page - 1)}>
				<span>&#8592; Prev</span>
			</button>
			<span style={{ fontSize: "13px", color: "#4b5362" }}>
				Page {page + 1} of {totalPages}
			</span>
			<button
				className='light'
				style={{ height: "34px", padding: "0 12px", fontSize: "13px" }}
				disabled={page >= totalPages - 1}
				onClick={() => onPageChange(page + 1)}>
				<span>Next &#8594;</span>
			</button>
		</div>
	);
};

// ====== Overview Tab ====== //

const OverviewTab = () => {
	const [loading, setLoading] = useState(false);
	const [overview, setOverview] = useState(null);
	const [fromDate, setFromDate] = useState(getMonthStart());
	const [toDate, setToDate] = useState(getToday());
	const [referrerType, setReferrerType] = useState("ALL");

	const fetchOverview = useCallback(async () => {
		setLoading(true);
		try {
			const params = buildQueryParams({ from: fromDate, to: toDate, referrerType });
			const resp = await fetch(
				`${API_URL}/api/1.0.0/admin/referral/overview?${params}`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						"X-JWT-Assertion": localStorage.getItem("token"),
					},
				}
			);
			if (resp.ok) {
				const json = await resp.json();
				setOverview(json.data || null);
			}
		} catch (e) {
			console.error("Error fetching referral overview", e);
		} finally {
			setLoading(false);
		}
	}, [fromDate, toDate, referrerType]);

	useEffect(() => {
		fetchOverview();
	}, []);

	const kpiCards = [
		{
			label: "Captured",
			value: overview?.captured ?? "-",
			color: "#f59e0b",
			icon: <CiUser />,
		},
		{
			label: "First Txn Done",
			value: overview?.firstTxnDone ?? "-",
			color: "#3b82f6",
			icon: <CiCircleCheck />,
		},
		{
			label: "Rewarded",
			value: overview?.rewarded ?? "-",
			color: "#10b981",
			icon: <CiStar />,
		},
		{
			label: "Pending Rewards",
			value: overview?.pendingRewards ?? "-",
			color: "#f59e0b",
			icon: <CiCircleList />,
		},
		{
			label: "Failed Rewards",
			value: overview?.failedRewards ?? "-",
			color: "#ef4444",
			icon: <CiCircleRemove />,
		},
		{
			label: "Points Credited",
			value: overview?.pointsCredited ?? "-",
			color: "#2980B9",
			icon: <CiStar />,
		},
		{
			label: "Commission Accrued",
			value: overview?.commissionAccruedCents
				? `ZAR ${formatAmountValue(overview.commissionAccruedCents / 100)}`
				: "-",
			color: "#7c3aed",
			icon: <CiTrophy />,
		},
		{
			label: "Commission Paid",
			value: overview?.commissionPaidCents
				? `ZAR ${formatAmountValue(overview.commissionPaidCents / 100)}`
				: "-",
			color: "#10b981",
			icon: <CiCircleCheck />,
		},
	];

	const funnelData = {
		labels: ["Captured", "First Txn Done", "Rewarded"],
		datasets: [
			{
				label: "Referral Funnel",
				data: [
					overview?.captured || 0,
					overview?.firstTxnDone || 0,
					overview?.rewarded || 0,
				],
				backgroundColor: ["#f59e0b", "#3b82f6", "#10b981"],
				borderRadius: 6,
			},
		],
	};

	const funnelOptions = {
		responsive: true,
		plugins: {
			legend: { display: false },
			title: { display: false },
			tooltip: { enabled: true },
		},
		scales: {
			y: { beginAtZero: true, grid: { color: "#f3f4f6" } },
			x: { grid: { display: false } },
		},
	};

	const splitData = {
		labels: ["Agent Referrals", "Customer Referrals"],
		datasets: [
			{
				data: [overview?.split?.agent || 0, overview?.split?.customer || 0],
				backgroundColor: ["#2980B9", "#10b981"],
				borderWidth: 2,
				borderColor: "#fff",
			},
		],
	};

	const splitOptions = {
		responsive: true,
		plugins: {
			legend: { position: "bottom" },
			tooltip: { enabled: true },
		},
	};

	return (
		<div className='referral-overview'>
			<FullPageLoader isActive={loading} />

			{/* Filters */}
			<div className='referral-filter-bar'>
				<div className='filter-group'>
					<label>From</label>
					<input
						type='date'
						value={fromDate}
						onChange={(e) => setFromDate(e.target.value)}
					/>
				</div>
				<div className='filter-group'>
					<label>To</label>
					<input
						type='date'
						value={toDate}
						onChange={(e) => setToDate(e.target.value)}
					/>
				</div>
				<div className='filter-group'>
					<label>Referral Type</label>
					<select
						value={referrerType}
						onChange={(e) => setReferrerType(e.target.value)}>
						<option value='ALL'>All Types</option>
						<option value='AGENT'>Agent</option>
						<option value='CUSTOMER'>Customer</option>
					</select>
				</div>
				<ButtonTextIcon
					title='Apply'
					icon={<CiFilter />}
					onClick={fetchOverview}
				/>
			</div>

			{/* KPI Cards */}
			<div className='referral-kpi-grid'>
				{kpiCards.map((card, i) => (
					<div className='referral-kpi-card' key={i}>
						<div
							className='kpi-icon'
							style={{ color: card.color, backgroundColor: card.color + "18" }}>
							{card.icon}
						</div>
						<div className='kpi-content'>
							<p className='kpi-label'>{card.label}</p>
							<h4 className='kpi-value' style={{ color: card.color }}>
								{card.value}
							</h4>
						</div>
					</div>
				))}
			</div>

			{/* Charts */}
			<div className='referral-charts-row'>
				<div className='referral-chart-box'>
					<h6 className='color-primary' style={{ marginBottom: "16px" }}>
						Referral Conversion Funnel
					</h6>
					<Bar data={funnelData} options={funnelOptions} height={120} />
				</div>
				<div className='referral-chart-box referral-chart-box--sm'>
					<h6 className='color-primary' style={{ marginBottom: "16px" }}>
						Agent vs Customer Split
					</h6>
					<Doughnut data={splitData} options={splitOptions} />
				</div>
			</div>
		</div>
	);
};

// ====== Rules Tab ====== //

const RuleSchema = Yup.object().shape({
	ruleName: Yup.string().required("Rule name is required"),
	referrerType: Yup.string().required("Referrer type is required"),
	rewardTrigger: Yup.string().required("Trigger is required"),
	referrerPoints: Yup.number().min(0).required("Referrer points required"),
	referredPoints: Yup.number().min(0).required("Referred points required"),
 	multiplier: Yup.number().min(1).required("Multiplier required"),
 	startAt: Yup.date()
 		.required("Start date required")
 		.max(Yup.ref('endAt'), 'Start must be before or equal to End'),
 	endAt: Yup.date()
 		.required("End date required")
 		.test('end-at-future', 'End time cannot be in the past.', function (value) {
 			if (!value) return false;
 			return new Date(value) >= new Date();
 		}),
 	priority: Yup.number().min(1).required("Priority required"),
	maxRewardCount: Yup.number().min(1).required("Max reward count required"),
	maxTotalRewardAmountCents: Yup.number().min(0).nullable(),
	autoPayEnabled: Yup.boolean(),
});

const RuleFormModal = ({ rule, onClose, onSaved }) => {
	const { addToastMessage } = useUI();
	const [loading, setLoading] = useState(false);
	const [box] = useOutsideClick({ state: true, callBack: onClose });

	const initialValues = {
		ruleName: rule?.ruleName || "",
		referrerType: rule?.referrerType || "CUSTOMER",
		rewardTrigger: rule?.rewardTrigger || "FIRST_TXN",
		minTransactionAmountCents: rule?.minTransactionAmountCents || "",
		referrerPoints: rule?.referrerPoints || 0,
		referredPoints: rule?.referredPoints || 0,
		multiplier: rule?.multiplier || 1,
		startAt: rule?.startAt ? rule.startAt.substring(0, 16) : "",
		endAt: rule?.endAt ? rule.endAt.substring(0, 16) : "",
		isActive: rule?.isActive !== undefined ? rule.isActive : true,
		priority: rule?.priority || 100,
		maxRewardCount: rule?.maxRewardCount || 1000,
		maxTotalRewardAmountCents: rule?.maxTotalRewardAmountCents || "",
		autoPayEnabled: rule?.autoPayEnabled !== undefined ? rule.autoPayEnabled : true,
		notes: rule?.notes || "",
	};

	const nowLocal = formatForDatetimeLocal();

	const handleSubmit = async (values) => {
		setLoading(true);
		const payload = {
			...values,
			referrerPoints: parseInt(values.referrerPoints),
			referredPoints: parseInt(values.referredPoints),
			multiplier: parseInt(values.multiplier),
			priority: parseInt(values.priority),
			maxRewardCount: parseInt(values.maxRewardCount),
			maxTotalRewardAmountCents: values.maxTotalRewardAmountCents
				? parseInt(values.maxTotalRewardAmountCents)
				: null,
			autoPayEnabled: values.autoPayEnabled,
			minTransactionAmountCents: values.minTransactionAmountCents
				? parseInt(values.minTransactionAmountCents)
				: null,
			startAt: values.startAt + ":00",
			endAt: values.endAt + ":00",
		};
		try {
			const url = rule?.id
				? `${API_URL}/api/1.0.0/admin/referral/rules/${rule.id}`
				: `${API_URL}/api/1.0.0/admin/referral/rules`;
			const method = rule?.id ? "PUT" : "POST";
			const resp = await fetch(url, {
				method,
				headers: {
					"Content-Type": "application/json",
					"X-JWT-Assertion": localStorage.getItem("token"),
				},
				body: JSON.stringify(payload),
			});
			if (resp.ok) {
				addToastMessage({
					status: true,
					message: rule?.id
						? "Rule updated successfully."
						: "Rule created successfully.",
				});
				onSaved();
				onClose();
			} else {
				addToastMessage({ status: false, message: "Failed to save rule." });
			}
		} catch (e) {
			console.error(e);
			addToastMessage({ status: false, message: "An error occurred." });
		} finally {
			setLoading(false);
		}
	};

	return (
		<PopUpModal>
			<section id='rs-modal'>
				<div className='inner'>
					<div
						className='box'
						ref={box}
						style={{ width: "600px", maxHeight: "90vh", overflowY: "auto" }}>
						<div style={{ padding: "32px" }}>
							<h5 className='color-primary'>
								{rule?.id ? "Edit Referral Rule" : "Create Referral Rule"}
							</h5>
							<br />
							<Formik
								initialValues={initialValues}
								validationSchema={RuleSchema}
								onSubmit={handleSubmit}>
								{({ isSubmitting, values }) => (
									<Form>
										<div className='referral-form-grid'>
											<div className='referral-form-field'>
												<label>Rule Name *</label>
												<Field
													name='ruleName'
													placeholder='e.g. Christmas 2x 2026'
												/>
												<ErrorMessage
													name='ruleName'
													component='p'
													className='error-messages'
												/>
											</div>
											<div className='referral-form-field'>
												<label>Referrer Type *</label>
												<Field as='select' name='referrerType'>
													<option value='CUSTOMER'>Customer</option>
													<option value='AGENT'>Agent</option>
												</Field>
												<ErrorMessage
													name='referrerType'
													component='p'
													className='error-messages'
												/>
											</div>
											<div className='referral-form-field'>
												<label>Reward Trigger *</label>
												<Field as='select' name='rewardTrigger'>
													<option value='FIRST_TXN'>Transaction</option>
													<option value='ONBOARDING' disabled={true}>Onboarding</option>
													<option value='MILESTONE' disabled={true}>Milestone</option>
												</Field>
												<ErrorMessage
													name='rewardTrigger'
													component='p'
													className='error-messages'
												/>
											</div>
											<div className='referral-form-field'>
												<label>Min Txn Amount (cents)</label>
												<Field
													name='minTransactionAmountCents'
													type='number'
													placeholder='Optional (e.g. 5000 = ZAR 50)'
												/>
											</div>
											<div className='referral-form-field'>
												<label>Referrer Points *</label>
												<Field
													name='referrerPoints'
													type='number'
													min='0'
												/>
												<ErrorMessage
													name='referrerPoints'
													component='p'
													className='error-messages'
												/>
											</div>
											<div className='referral-form-field'>
												<label>Referred Points *</label>
												<Field
													name='referredPoints'
													type='number'
													min='0'
												/>
												<ErrorMessage
													name='referredPoints'
													component='p'
													className='error-messages'
												/>
											</div>
											<div className='referral-form-field'>
												<label>Multiplier *</label>
												<Field as='select' name='multiplier'>
													<option value={1}>1x (Standard)</option>
													<option value={2}>2x</option>
													<option value={3}>3x</option>
													<option value={4}>4x</option>
												</Field>
												<ErrorMessage
													name='multiplier'
													component='p'
													className='error-messages'
												/>
											</div>
											<div className='referral-form-field'>
												<label>Priority *</label>
												<Field
													name='priority'
													type='number'
													min='1'
													placeholder='Higher = more specific'
												/>
												<ErrorMessage
													name='priority'
													component='p'
													className='error-messages'
												/>
											</div>
											<div className='referral-form-field'>
												<label>Max Reward Count *</label>
												<Field
													name='maxRewardCount'
													type='number'
													min='1'
												/>
												<ErrorMessage
													name='maxRewardCount'
													component='p'
													className='error-messages'
												/>
											</div>
											<div className='referral-form-field'>
												<label>Max Total Reward (cents)</label>
												<Field
													name='maxTotalRewardAmountCents'
													type='number'
													min='0'
													placeholder='Optional'
												/>
												<ErrorMessage
													name='maxTotalRewardAmountCents'
													component='p'
													className='error-messages'
												/>
											</div>
											<div className='referral-form-field'>
												<label className='flex center-left rs-checkbox-label' style={{ gap: "10px", marginTop: '28px' }}>
													<Field name='autoPayEnabled' type='checkbox' className='rs-checkbox' />
													Auto Pay Enabled
												</label>
											</div>
											<div className='referral-form-field'>
												<label>Start Date & Time *</label>
												<Field name='startAt'>
													{({ field, form }) => (
														<input
															{...field}
															type='datetime-local'
															max={form.values.endAt || undefined}
														/>
													)}
												</Field>
												<ErrorMessage
													name='startAt'
													component='p'
													className='error-messages'
												/>
											</div>
											<div className='referral-form-field'>
												<label>End Date & Time *</label>
												<Field name='endAt'>
													{({ field,form }) => (
														<input
															{...field}
															type='datetime-local'
															min={form.values.startAt && nowLocal}
														/>
													)}
												</Field>
												<ErrorMessage
													name='endAt'
													component='p'
													className='error-messages'
												/>
											</div>
											<div
												className='referral-form-field'
												style={{ gridColumn: "1 / -1" }}>
												<label>Notes</label>
												<Field
													name='notes'
													as='textarea'
													placeholder='Optional notes'
												/>
											</div>
											<div
												className='referral-form-field'
												style={{ gridColumn: "1 / -1" }}>
												<label className='flex center-left rs-checkbox-label' style={{ gap: "10px" }}>
													<Field name='isActive' type='checkbox' className='rs-checkbox' />
													Active Rule
												</label>
											</div>
										</div>
										<div
											className='flex'
											style={{
												marginTop: "24px",
												gap: "12px",
												justifyContent: "flex-end",
											}}>
											<ButtonLightTextIcon
												title='Cancel'
												icon={<CiCircleRemove />}
												type='button'
												className='modal-action'
												onClick={onClose}
											/>
											<ButtonTextIcon
												title={loading ? "Saving..." : "Save Rule"}
												icon={<CiCircleCheck />}
												type='submit'
												className='modal-action'
												disabled={isSubmitting || loading}
											/>
										</div>
									</Form>
								)}
							</Formik>
						</div>
					</div>
				</div>
			</section>
		</PopUpModal>
	);
};

const RulesTab = () => {
	const [loading, setLoading] = useState(false);
	const [rules, setRules] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [pageSize] = useState(20);
	const [filterActive, setFilterActive] = useState("");
	const [showForm, setShowForm] = useState(false);
	const [editRule, setEditRule] = useState(null);
	const [actionLoading, setActionLoading] = useState(null);
	const { addToastMessage } = useUI();
	const userRole = decodeRolesFromToken()[0];

	const fetchRules = useCallback(async () => {
		setLoading(true);
		try {
			const params = buildQueryParams({
				page,
				size: pageSize,
				...(filterActive !== "" ? { active: filterActive } : {}),
			});
			const resp = await fetch(
				`${API_URL}/api/1.0.0/admin/referral/rules?${params}`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						"X-JWT-Assertion": localStorage.getItem("token"),
					},
				}
			);
			if (resp.ok) {
				const json = await resp.json();
				setRules(json.data?.items || json.data || []);
				setTotal(json.data?.total || 0);
			}
		} catch (e) {
			console.error("Error fetching rules", e);
		} finally {
			setLoading(false);
		}
	}, [page, pageSize, filterActive]);

	useEffect(() => {
		fetchRules();
	}, [fetchRules]);

	const toggleRule = async (ruleId, activate) => {
		setActionLoading(ruleId);
		try {
			const action = activate ? "activate" : "deactivate";
			const resp = await fetch(
				`${API_URL}/api/1.0.0/admin/referral/rules/${ruleId}/${action}`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"X-JWT-Assertion": localStorage.getItem("token"),
					},
				}
			);
			if (resp.ok) {
				addToastMessage({
					status: true,
					message: `Rule ${activate ? "activated" : "deactivated"} successfully.`,
				});
				fetchRules();
			} else {
				addToastMessage({ status: false, message: "Failed to update rule status." });
			}
		} catch (e) {
			console.error(e);
			addToastMessage({ status: false, message: "An error occurred." });
		} finally {
			setActionLoading(null);
		}
	};

	const totalPages = Math.ceil(total / pageSize);
	const canManage = [1, 6].includes(userRole);

	return (
		<div>
			<FullPageLoader isActive={loading} />

			<div className='referral-filter-bar'>
				<div className='filter-group'>
					<label>Status</label>
					<select
						value={filterActive}
						onChange={(e) => {
							setFilterActive(e.target.value);
							setPage(0);
						}}>
						<option value=''>All Rules</option>
						<option value='true'>Active</option>
						<option value='false'>Inactive</option>
					</select>
				</div>
				<ButtonLightTextIcon
					title='Refresh'
					icon={<CiRedo />}
					onClick={fetchRules}
				/>
				{canManage && (
					<ButtonTextIcon
						title='Create Rule'
						icon={<CiCirclePlus />}
						onClick={() => {
							setEditRule(null);
							setShowForm(true);
						}}
					/>
				)}
			</div>

			<div className='rs-table-holder'>
				<table className='rs-table'>
					<thead>
						<tr>
							<th>#</th>
							<th>Rule Name</th>
							<th>Referrer Type</th>
							<th>Trigger</th>
							<th>Min Txn (ZAR)</th>
							<th>Referrer Pts</th>
							<th>Referred Pts</th>
							<th>Multiplier</th>
							<th>Priority</th>
							<th>Max Count</th>
							<th>Max Amount (ZAR)</th>
							<th>Auto Pay</th>
							<th>Start Date</th>
							<th>End Date</th>
							<th>Status</th>
							{canManage && <th>Actions</th>}
						</tr>
					</thead>
					<tbody>
						{rules.length > 0 ? (
							rules.map((rule, i) => (
								<tr key={rule.id || i}>
									<td>{page * pageSize + i + 1}</td>
									<td>
										<strong>{rule.ruleName}</strong>
									</td>
									<td>{rule.referrerType}</td>
									<td>{rule.rewardTrigger}</td>
									<td>
										{rule.minTransactionAmountCents
											? formatAmountValue(rule.minTransactionAmountCents / 100)
											: "-"}
									</td>
									<td>{rule.referrerPoints}</td>
									<td>{rule.referredPoints}</td>
									<td>{rule.multiplier}x</td>
									<td>{rule.priority}</td>
									<td>{rule.maxRewardCount || "-"}</td>
									<td>
										{rule.maxTotalRewardAmountCents
											? formatAmountValue(rule.maxTotalRewardAmountCents / 100)
											: "-"}
									</td>
									<td>{rule.autoPayEnabled ? "YES" : "NO"}</td>
									<td>
										{rule.startAt ? formatDateTime(rule.startAt) : "-"}
									</td>
									<td>
										{rule.endAt ? formatDateTime(rule.endAt) : "-"}
									</td>
									<td>
										<StatusBadge
											status={rule.isActive ? "ACTIVE" : "INACTIVE"}
										/>
									</td>
									{canManage && (
										<td>
											<div className='flex center-left' style={{ gap: "8px" }}>
												<ButtonLightTextIcon
													title='Edit'
													icon={<CiSettings />}
													onClick={() => {
														setEditRule(rule);
														setShowForm(true);
													}}
												/>
												<ButtonLightTextIcon
													title={rule.isActive ? "Deactivate" : "Activate"}
													icon={
														rule.isActive ? <CiCircleRemove /> : <CiCircleCheck />
													}
													disabled={actionLoading === rule.id}
													onClick={() => toggleRule(rule.id, !rule.isActive)}
												/>
											</div>
										</td>
									)}
								</tr>
							))
						) : (
							<tr>
								<td
									colSpan={canManage ? 16 : 15}
									style={{ textAlign: "center", padding: "20px" }}>
									<p>No referral rules found.</p>
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
			<SimplePagination
				page={page}
				totalPages={totalPages}
				onPageChange={setPage}
			/>

			{showForm && (
				<RuleFormModal
					rule={editRule}
					onClose={() => setShowForm(false)}
					onSaved={fetchRules}
				/>
			)}
		</div>
	);
};

// ====== Rewards Ledger Tab ====== //

const RewardsLedgerTab = () => {
	const [loading, setLoading] = useState(false);
	const [rewards, setRewards] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [pageSize] = useState(20);
	const [filterStatus, setFilterStatus] = useState("");
	const [filterTrigger, setFilterTrigger] = useState("");
	const [fromDate, setFromDate] = useState("");
	const [toDate, setToDate] = useState("");
	const [actionLoading, setActionLoading] = useState(null);
	const { addToastMessage } = useUI();
	const userRole = decodeRolesFromToken()[0];

	const fetchRewards = useCallback(async () => {
		setLoading(true);
		try {
			const params = buildQueryParams({
				page,
				size: pageSize,
				status: filterStatus,
				trigger: filterTrigger,
				from: fromDate,
				to: toDate,
			});
			const resp = await fetch(
				`${API_URL}/api/1.0.0/admin/referral/rewards?${params}`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						"X-JWT-Assertion": localStorage.getItem("token"),
					},
				}
			);
			if (resp.ok) {
				const json = await resp.json();
				setRewards(json.data?.items || json.data || []);
				setTotal(json.data?.total || 0);
			}
		} catch (e) {
			console.error("Error fetching rewards ledger", e);
		} finally {
			setLoading(false);
		}
	}, [page, pageSize, filterStatus, filterTrigger, fromDate, toDate]);

	useEffect(() => {
		fetchRewards();
	}, [fetchRewards]);

	const retryReward = async (rewardId) => {
		setActionLoading(rewardId);
		try {
			const resp = await fetch(
				`${API_URL}/api/1.0.0/admin/referral/rewards/${rewardId}/retry`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"X-JWT-Assertion": localStorage.getItem("token"),
					},
				}
			);
			if (resp.ok) {
				addToastMessage({
					status: true,
					message: "Reward credit retry initiated.",
				});
				fetchRewards();
			} else {
				addToastMessage({ status: false, message: "Failed to retry reward credit." });
			}
		} catch (e) {
			console.error(e);
			addToastMessage({ status: false, message: "An error occurred." });
		} finally {
			setActionLoading(null);
		}
	};

	const totalPages = Math.ceil(total / pageSize);
	const canRetry = [1, 9].includes(userRole);

	return (
		<div>
			<FullPageLoader isActive={loading} />

			<div className='referral-filter-bar'>
				<div className='filter-group'>
					<label>Status</label>
					<select
						value={filterStatus}
						onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}>
						<option value=''>All Statuses</option>
						<option value='PENDING'>Pending</option>
						<option value='CREDITED'>Credited</option>
						<option value='FAILED'>Failed</option>
						<option value='REVERSED'>Reversed</option>
					</select>
				</div>
				<div className='filter-group'>
					<label>Trigger</label>
					<select
						value={filterTrigger}
						onChange={(e) => { setFilterTrigger(e.target.value); setPage(0); }}>
						<option value=''>All Triggers</option>
						<option value='FIRST_TXN'>First Transaction</option>
						<option value='ONBOARDING'>Onboarding</option>
						<option value='MILESTONE'>Milestone</option>
					</select>
				</div>
				<div className='filter-group'>
					<label>From</label>
					<input
						type='date'
						value={fromDate}
						onChange={(e) => setFromDate(e.target.value)}
					/>
				</div>
				<div className='filter-group'>
					<label>To</label>
					<input
						type='date'
						value={toDate}
						onChange={(e) => setToDate(e.target.value)}
					/>
				</div>
				<ButtonLightTextIcon
					title='Refresh'
					icon={<CiRedo />}
					onClick={fetchRewards}
				/>
			</div>

			<div style={{ padding: "0 20px 8px 20px" }}>
				<p style={{ fontSize: "13px", color: "#4b5362" }}>
					Total Records: <strong>{total}</strong>
				</p>
			</div>

			<div className='rs-table-holder'>
				<table className='rs-table'>
					<thead>
						<tr>
							<th>#</th>
							<th>Created At</th>
							<th>Reward ID</th>
							<th>Program</th>
							<th>Trigger</th>
							<th>Beneficiary</th>
							<th>Counterparty</th>
							<th>Points</th>
							<th>Txn ID</th>
							<th>Status</th>
							<th>Credited At</th>
							<th>Rule Applied</th>
							{canRetry && <th>Actions</th>}
						</tr>
					</thead>
					<tbody>
						{rewards.length > 0 ? (
							rewards.map((r, i) => (
								<tr key={r.id || i}>
									<td>{page * pageSize + i + 1}</td>
									<td>{r.createdAt ? formatDateTime(r.createdAt) : "-"}</td>
									<td>{r.id}</td>
									<td>
										<span style={{ fontSize: "12px" }}>
											{r.rewardProgram === "OCTOREWARDS_POINTS"
												? "OctoRewards"
												: r.rewardProgram === "AGENT_COMMISSION"
												? "Commission"
												: r.rewardProgram || "-"}
										</span>
									</td>
									<td>{r.rewardTrigger || "-"}</td>
									<td>
										<div>
											<strong style={{ fontSize: "13px" }}>
												{r.beneficiaryName || `User #${r.beneficiaryUserId}` || "-"}
											</strong>
											<p style={{ fontSize: "11px", color: "#8f8f8f" }}>
												ID: {r.beneficiaryUserId}
											</p>
										</div>
									</td>
									<td>
										<div>
											<strong style={{ fontSize: "13px" }}>
												{r.counterpartyName || `User #${r.counterpartyUserId}` || "-"}
											</strong>
											<p style={{ fontSize: "11px", color: "#8f8f8f" }}>
												ID: {r.counterpartyUserId}
											</p>
										</div>
									</td>
									<td>{r.points ?? "-"}</td>
									<td>{r.transactionId || "-"}</td>
									<td>
										<StatusBadge status={r.status} />
									</td>
									<td>
										{r.creditedAt ? formatDateTime(r.creditedAt) : "-"}
									</td>
									<td>
										<span style={{ fontSize: "12px", color: "#4b5362" }}>
											{r.appliedRuleName || "-"}
										</span>
									</td>
									{canRetry && (
										<td>
											{["FAILED", "PENDING"].includes(r.status) && (
												<ButtonLightTextIcon
													title={actionLoading === r.id ? "Retrying..." : "Retry"}
													icon={<CiRedo />}
													disabled={actionLoading === r.id}
													onClick={() => retryReward(r.id)}
												/>
											)}
										</td>
									)}
								</tr>
							))
						) : (
							<tr>
								<td
									colSpan={canRetry ? 13 : 12}
									style={{ textAlign: "center", padding: "20px" }}>
									<p>No reward records found.</p>
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
			<SimplePagination
				page={page}
				totalPages={totalPages}
				onPageChange={setPage}
			/>
		</div>
	);
};

// ====== Agent Commission Tab ====== //

// ---- Bulk action modal (shared for Mark Paid / Reject) ----
const BulkActionModal = ({ type, onConfirm, onCancel, loading }) => {
	const [inputValue, setInputValue] = useState("");
	const box = useOutsideClick(onCancel);
	const isMarkPaid = type === "mark-paid";

	return (
		<PopUpModal>
			<section id='rs-modal'>
				<div className='inner'>
					<div className='box' ref={box} style={{ width: "460px" }}>
						<div style={{ padding: "28px 32px" }}>
							<h5 className='color-primary'>
								{isMarkPaid ? "Bulk Mark as Paid" : "Bulk Reject Commissions"}
							</h5>
							<p style={{ marginTop: "8px", fontSize: "13px", color: "#6b7280" }}>
								{isMarkPaid
									? "Optionally provide a payout reference (e.g. NEFT-20260303-001) for this batch payment."
									: "Optionally provide a reason for rejecting the selected commissions."}
							</p>
							<div style={{ marginTop: "20px" }}>
								<label
									style={{
										display: "block",
										fontSize: "11px",
										fontWeight: 600,
										textTransform: "uppercase",
										letterSpacing: "0.4px",
										color: "#4b5362",
										marginBottom: "6px",
									}}>
									{isMarkPaid ? "Payout Reference" : "Rejection Reason"}{" "}
									<span style={{ color: "#9ca3af", fontWeight: 400 }}>
										(optional)
									</span>
								</label>
								<input
									type='text'
									value={inputValue}
									onChange={(e) => setInputValue(e.target.value)}
									placeholder={
										isMarkPaid
											? "e.g. NEFT-20260303-001"
											: "e.g. Duplicate entry"
									}
									style={{ width: "100%", fontSize: "13px" }}
									autoFocus
								/>
							</div>
							<div
								className='flex center-left'
								style={{ gap: "10px", marginTop: "24px" }}>
								<ButtonTextIcon
									title={isMarkPaid ? "Confirm Mark Paid" : "Confirm Reject"}
									icon={isMarkPaid ? <CiCircleCheck /> : <CiCircleRemove />}
									disabled={loading}
									onClick={() => onConfirm(inputValue.trim())}
								/>
								<ButtonLightTextIcon
									title='Cancel'
									icon={<CiCircleRemove />}
									disabled={loading}
									onClick={onCancel}
								/>
							</div>
						</div>
					</div>
				</div>
			</section>
		</PopUpModal>
	);
};

const AgentCommissionTab = () => {
	const [loading, setLoading] = useState(false);
	const [commissions, setCommissions] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [pageSize] = useState(20);
	const [filterStatus, setFilterStatus] = useState("");
	const [filterMonth, setFilterMonth] = useState("");
	const [filterAgentId, setFilterAgentId] = useState("");
	const [actionLoading, setActionLoading] = useState(null);
	const [selectedIds, setSelectedIds] = useState(new Set());
	const [bulkActionLoading, setBulkActionLoading] = useState(false);
	const [bulkModal, setBulkModal] = useState(null); // null | "mark-paid" | "reject"
	const [exportLoading, setExportLoading] = useState(false);
	const { addToastMessage } = useUI();
	const userRole = decodeRolesFromToken()[0];

	const fetchCommissions = useCallback(async () => {
		setLoading(true);
		try {
			const params = buildQueryParams({
				page,
				size: pageSize,
				status: filterStatus,
				periodMonth: filterMonth,
				agentId: filterAgentId,
			});
			const resp = await fetch(
				`${API_URL}/api/1.0.0/admin/referral/agent-commissions?${params}`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						"X-JWT-Assertion": localStorage.getItem("token"),
					},
				}
			);
			if (resp.ok) {
				const json = await resp.json();
				setCommissions(json.data?.items || json.data || []);
				setTotal(json.data?.total || 0);
				setSelectedIds(new Set());
			}
		} catch (e) {
			console.error("Error fetching commissions", e);
		} finally {
			setLoading(false);
		}
	}, [page, pageSize, filterStatus, filterMonth, filterAgentId]);

	useEffect(() => {
		fetchCommissions();
	}, [fetchCommissions]);

	// ---- Single-row actions (unchanged) ----
	const performAction = async (commissionId, action) => {
		setActionLoading(`${commissionId}-${action}`);
		try {
			const resp = await fetch(
				`${API_URL}/api/1.0.0/admin/referral/agent-commissions/${commissionId}/${action}`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"X-JWT-Assertion": localStorage.getItem("token"),
					},
				}
			);
			if (resp.ok) {
				const msgs = {
					approve: "Commission approved.",
					"mark-paid": "Commission marked as paid.",
					reject: "Commission rejected.",
				};
				addToastMessage({
					status: true,
					message: msgs[action] || "Action successful.",
				});
				fetchCommissions();
			} else {
				addToastMessage({ status: false, message: "Failed to perform action." });
			}
		} catch (e) {
			console.error(e);
			addToastMessage({ status: false, message: "An error occurred." });
		} finally {
			setActionLoading(null);
		}
	};

	// ---- Shared bulk response handler ----
	const handleBulkResponse = (json, actionLabel) => {
		const { successCount = 0, skippedCount = 0, skipped = [] } = json.data || {};
		if (successCount > 0) {
			addToastMessage({
				status: true,
				message: `${successCount} commission(s) ${actionLabel} successfully.${skippedCount > 0 ? ` ${skippedCount} skipped.` : ""}`,
			});
		}
		if (skippedCount > 0 && successCount === 0) {
			addToastMessage({
				status: false,
				message: `All ${skippedCount} commission(s) were skipped. ${skipped[0]?.reason || ""}`,
			});
		}
	};

	// ---- Bulk Approve ----
	const performBulkApprove = async () => {
		setBulkActionLoading(true);
		try {
			const resp = await fetch(
				`${API_URL}/api/1.0.0/admin/referral/agent-commissions/bulk-approve`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"X-JWT-Assertion": localStorage.getItem("token"),
					},
					body: JSON.stringify({ ids: Array.from(selectedIds) }),
				}
			);
			if (resp.ok) {
				const json = await resp.json();
				handleBulkResponse(json, "approved");
			} else {
				addToastMessage({ status: false, message: "Bulk approve failed." });
			}
		} catch (e) {
			console.error(e);
			addToastMessage({ status: false, message: "An error occurred." });
		} finally {
			setBulkActionLoading(false);
			fetchCommissions();
		}
	};

	// ---- Bulk Mark Paid ----
	const performBulkMarkPaid = async (payoutReference) => {
		setBulkActionLoading(true);
		try {
			const body = { ids: Array.from(selectedIds) };
			if (payoutReference) body.payoutReference = payoutReference;
			const resp = await fetch(
				`${API_URL}/api/1.0.0/admin/referral/agent-commissions/bulk-mark-paid`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"X-JWT-Assertion": localStorage.getItem("token"),
					},
					body: JSON.stringify(body),
				}
			);
			if (resp.ok) {
				const json = await resp.json();
				handleBulkResponse(json, "marked as paid");
			} else {
				addToastMessage({ status: false, message: "Bulk mark-paid failed." });
			}
		} catch (e) {
			console.error(e);
			addToastMessage({ status: false, message: "An error occurred." });
		} finally {
			setBulkActionLoading(false);
			setBulkModal(null);
			fetchCommissions();
		}
	};

	// ---- Bulk Reject ----
	const performBulkReject = async (reason) => {
		setBulkActionLoading(true);
		try {
			const body = { ids: Array.from(selectedIds) };
			if (reason) body.reason = reason;
			const resp = await fetch(
				`${API_URL}/api/1.0.0/admin/referral/agent-commissions/bulk-reject`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"X-JWT-Assertion": localStorage.getItem("token"),
					},
					body: JSON.stringify(body),
				}
			);
			if (resp.ok) {
				const json = await resp.json();
				handleBulkResponse(json, "rejected");
			} else {
				addToastMessage({ status: false, message: "Bulk reject failed." });
			}
		} catch (e) {
			console.error(e);
			addToastMessage({ status: false, message: "An error occurred." });
		} finally {
			setBulkActionLoading(false);
			setBulkModal(null);
			fetchCommissions();
		}
	};

	// ---- Server-side Export (all filtered records) ----
	const exportFromServer = async () => {
		setExportLoading(true);
		try {
			const params = buildQueryParams({
				periodMonth: filterMonth,
				status: filterStatus,
			});
			const resp = await fetch(
				`${API_URL}/api/1.0.0/admin/referral/agent-commissions/export${params ? `?${params}` : ""}`,
				{
					method: "GET",
					headers: {
						"X-JWT-Assertion": localStorage.getItem("token"),
					},
				}
			);
			if (resp.ok) {
				const json = await resp.json();
				const { filename, contentType, fileContent } = json.data;
				const byteArray = Uint8Array.from(atob(fileContent), (c) =>
					c.charCodeAt(0)
				);
				const blob = new Blob([byteArray], { type: contentType });
				const url = URL.createObjectURL(blob);
				const a = document.createElement("a");
				a.href = url;
				a.download = filename;
				a.click();
				URL.revokeObjectURL(url);
			} else {
				addToastMessage({ status: false, message: "Export failed." });
			}
		} catch (e) {
			console.error(e);
			addToastMessage({ status: false, message: "Export failed." });
		} finally {
			setExportLoading(false);
		}
	};

	// ---- Local XLSX for selected rows ----
	const downloadSelectedXlsx = (rows) => {
		const headers = [
			"#",
			"Period",
			"Agent ID",
			"Agent",
			"Referred Customer",
			"Basis",
			"Commission (ZAR)",
			"Status",
			"Approved At",
			"Paid At",
		];
		const data = rows.map((c, i) => [
			i + 1,
			formatYYYYMM(String(c.periodMonth)),
			c.agentUserId,
			c.agentName || `Agent #${c.agentUserId}`,
			c.referredUserName || (c.referredUserId ? `#${c.referredUserId}` : "-"),
			c.commissionBasis || "-",
			c.commissionCents ? (c.commissionCents / 100).toFixed(2) : "0.00",
			c.status,
			c.approvedAt ? formatDateTime(c.approvedAt) : "-",
			c.paidAt ? formatDateTime(c.paidAt) : "-",
		]);
		const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
		ws["!cols"] = [
			{ wch: 5 },
			{ wch: 12 },
			{ wch: 10 },
			{ wch: 22 },
			{ wch: 22 },
			{ wch: 16 },
			{ wch: 18 },
			{ wch: 12 },
			{ wch: 22 },
			{ wch: 22 },
		];
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, "Agent Commissions");
		XLSX.writeFile(
			wb,
			`agent-commissions-selected-${new Date().toISOString().split("T")[0]}.xlsx`
		);
	};

	// ---- Selection helpers ----
	const allOnPageSelected =
		commissions.length > 0 && commissions.every((c) => selectedIds.has(c.id));
	const someOnPageSelected = commissions.some((c) => selectedIds.has(c.id));

	const toggleSelectAll = () => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (allOnPageSelected) {
				commissions.forEach((c) => next.delete(c.id));
			} else {
				commissions.forEach((c) => next.add(c.id));
			}
			return next;
		});
	};

	const toggleRow = (id) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	// ---- Bottom bar derived state ----
	const selectedCommissions = commissions.filter((c) => selectedIds.has(c.id));
	const selectedStatuses = [
		...new Set(selectedCommissions.map((c) => c.status)),
	];
	const commonStatus = selectedStatuses.length === 1 ? selectedStatuses[0] : null;
	const selectedTotalCents = selectedCommissions.reduce(
		(sum, c) => sum + (c.commissionCents || 0),
		0
	);
	const pageTotalCents = commissions.reduce(
		(sum, c) => sum + (c.commissionCents || 0),
		0
	);

	const totalPages = Math.ceil(total / pageSize);
	const canApprove = [1, 9].includes(userRole);
	const colCount = canApprove ? 11 : 10;

	return (
		<div style={{ position: "relative" }}>
			<FullPageLoader isActive={loading || exportLoading} />

			{/* ---- Modals ---- */}
			{bulkModal && (
				<BulkActionModal
					type={bulkModal}
					loading={bulkActionLoading}
					onCancel={() => setBulkModal(null)}
					onConfirm={(val) =>
						bulkModal === "mark-paid"
							? performBulkMarkPaid(val)
							: performBulkReject(val)
					}
				/>
			)}

			<div className='referral-filter-bar'>
				<div className='filter-group'>
					<label>Agent ID</label>
					<input
						type='text'
						placeholder='e.g. 1042'
						value={filterAgentId}
						onChange={(e) => {
							setFilterAgentId(e.target.value);
							setPage(0);
						}}
						style={{ minWidth: "120px" }}
					/>
				</div>
				<div className='filter-group'>
					<label>Period Month</label>
					<input
						type='month'
						value={
							filterMonth
								? `${filterMonth.substring(0, 4)}-${filterMonth.substring(4, 6)}`
								: ""
						}
						onChange={(e) => {
							if (e.target.value) {
								setFilterMonth(e.target.value.replace("-", ""));
							} else {
								setFilterMonth("");
							}
							setPage(0);
						}}
					/>
				</div>
				<div className='filter-group'>
					<label>Status</label>
					<select
						value={filterStatus}
						onChange={(e) => {
							setFilterStatus(e.target.value);
							setPage(0);
						}}>
						<option value=''>All Statuses</option>
						<option value='PENDING'>Pending</option>
						<option value='APPROVED'>Approved</option>
						<option value='PAID'>Paid</option>
						<option value='REVERSED'>Reversed</option>
					</select>
				</div>
				<ButtonLightTextIcon
					title='Refresh'
					icon={<CiRedo />}
					onClick={fetchCommissions}
				/>
				<ButtonLightTextIcon
					title={exportLoading ? "Exporting…" : "Export"}
					icon={<CiExport />}
					onClick={exportFromServer}
					disabled={exportLoading || commissions.length === 0}
				/>
			</div>

			<div className='commission-summary-bar'>
				<span>
					Total Records: <strong>{total}</strong>
				</span>
				<span>
					Page Commission Total:{" "}
					<strong>ZAR {formatAmountValue(pageTotalCents / 100)}</strong>
				</span>
			</div>

			<div className='rs-table-holder'>
				<table className='rs-table'>
					<thead>
						<tr>
							<th style={{ width: "40px", textAlign: "center" }}>
								<input
									type='checkbox'
									checked={allOnPageSelected}
									ref={(el) => {
										if (el)
											el.indeterminate =
												someOnPageSelected && !allOnPageSelected;
									}}
									onChange={toggleSelectAll}
									style={{ cursor: "pointer" }}
								/>
							</th>
							<th>#</th>
							<th>Period</th>
							<th>Agent</th>
							<th>Referred Customer</th>
							<th>Basis</th>
							<th>Commission (ZAR)</th>
							<th>Status</th>
							<th>Approved At</th>
							<th>Paid At</th>
							{canApprove && <th>Actions</th>}
						</tr>
					</thead>
					<tbody>
						{commissions.length > 0 ? (
							commissions.map((c, i) => (
								<tr
									key={c.id || i}
									className={selectedIds.has(c.id) ? "row-selected" : ""}
									onClick={() => toggleRow(c.id)}
									style={{ cursor: "pointer" }}>
									<td
										style={{ textAlign: "center" }}
										onClick={(e) => e.stopPropagation()}>
										<input
											type='checkbox'
											checked={selectedIds.has(c.id)}
											onChange={() => toggleRow(c.id)}
											style={{ cursor: "pointer" }}
										/>
									</td>
									<td>{page * pageSize + i + 1}</td>
									<td>{formatYYYYMM(String(c.periodMonth))}</td>
									<td>
										<div>
											<strong style={{ fontSize: "13px" }}>
												{c.agentName || `Agent #${c.agentUserId}`}
											</strong>
											<p style={{ fontSize: "11px", color: "#8f8f8f" }}>
												ID: {c.agentUserId}
											</p>
										</div>
									</td>
									<td>
										{c.referredUserName || (c.referredUserId ? `#${c.referredUserId}` : "-")}
									</td>
									<td>{c.commissionBasis || "-"}</td>
									<td>
										ZAR{" "}
										{c.commissionCents
											? formatAmountValue(c.commissionCents / 100)
											: "0.00"}
									</td>
									<td>
										<StatusBadge status={c.status} />
									</td>
									<td>{c.approvedAt ? formatDateTime(c.approvedAt) : "-"}</td>
									<td>{c.paidAt ? formatDateTime(c.paidAt) : "-"}</td>
									{canApprove && (
										<td onClick={(e) => e.stopPropagation()}>
											<div className='flex center-left' style={{ gap: "6px" }}>
												{c.status === "PENDING" && (
													<ButtonLightTextIcon
														title='Approve'
														icon={<CiCircleCheck />}
														disabled={actionLoading?.startsWith(c.id)}
														onClick={() => performAction(c.id, "approve")}
													/>
												)}
												{c.status === "APPROVED" && (
													<ButtonTextIcon
														title='Mark Paid'
														icon={<CiCircleCheck />}
														disabled={actionLoading?.startsWith(c.id)}
														onClick={() => performAction(c.id, "mark-paid")}
													/>
												)}
												{["PENDING", "APPROVED"].includes(c.status) && (
													<ButtonLightTextIcon
														title='Reject'
														icon={<CiCircleRemove />}
														disabled={actionLoading?.startsWith(c.id)}
														onClick={() => performAction(c.id, "reject")}
													/>
												)}
											</div>
										</td>
									)}
								</tr>
							))
						) : (
							<tr>
								<td
									colSpan={colCount}
									style={{ textAlign: "center", padding: "20px" }}>
									<p>No commission records found.</p>
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
			<SimplePagination
				page={page}
				totalPages={totalPages}
				onPageChange={setPage}
			/>

			{/* ---- Bottom Selection Bar ---- */}
			{selectedIds.size > 0 && (
				<div className='commission-bottom-bar'>
					<div className='commission-bottom-bar__info'>
						<span className='commission-bottom-bar__count'>
							{selectedIds.size} row{selectedIds.size !== 1 ? "s" : ""} selected
						</span>
						<span className='commission-bottom-bar__total'>
							Selected Total:{" "}
							<strong>ZAR {formatAmountValue(selectedTotalCents / 100)}</strong>
						</span>
						{!commonStatus && selectedIds.size > 1 && (
							<span className='commission-bottom-bar__mixed'>
								Mixed statuses — bulk actions unavailable
							</span>
						)}
					</div>
					<div className='commission-bottom-bar__actions'>
						{canApprove && commonStatus === "PENDING" && (
							<ButtonLightTextIcon
								title='Approve Selected'
								icon={<CiCircleCheck />}
								disabled={bulkActionLoading}
								onClick={performBulkApprove}
							/>
						)}
						{canApprove && commonStatus === "APPROVED" && (
							<ButtonTextIcon
								title='Mark Paid'
								icon={<CiCircleCheck />}
								disabled={bulkActionLoading}
								onClick={() => setBulkModal("mark-paid")}
							/>
						)}
						{canApprove && ["PENDING", "APPROVED"].includes(commonStatus) && (
							<ButtonLightTextIcon
								title='Reject Selected'
								icon={<CiCircleRemove />}
								disabled={bulkActionLoading}
								onClick={() => setBulkModal("reject")}
							/>
						)}
						<ButtonLightTextIcon
							title='Download Selected'
							icon={<CiExport />}
							onClick={() => downloadSelectedXlsx(selectedCommissions)}
						/>
						<button
							className='commission-bottom-bar__clear'
							onClick={() => setSelectedIds(new Set())}>
							Clear
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

// ====== Agent Performance Tab ====== //

// Podium visual slots rendered left → right: 2nd, 1st, 3rd
const PODIUM_SLOTS = [2, 1, 3];
// Keyed by rank number (1-based)
const RANK_META = {
	1: { emoji: "🥇", label: "1st", color: "#f59e0b", shadow: "0 8px 24px rgba(245,158,11,0.25)" },
	2: { emoji: "🥈", label: "2nd", color: "#94a3b8", shadow: "0 8px 24px rgba(148,163,184,0.25)" },
	3: { emoji: "🥉", label: "3rd", color: "#cd7f32", shadow: "0 8px 24px rgba(205,127,50,0.25)" },
};

const ConversionBar = ({ rate }) => (
	<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
		<div
			style={{
				flex: 1,
				height: "6px",
				background: "#f3f4f6",
				borderRadius: "3px",
				overflow: "hidden",
				minWidth: "60px",
			}}>
			<div
				style={{
					width: `${Math.min(rate || 0, 100)}%`,
					height: "100%",
					background: "linear-gradient(90deg, #3b82f6, #8b5cf6)",
					borderRadius: "3px",
					transition: "width 0.4s ease",
				}}
			/>
		</div>
		<span style={{ fontSize: "12px", fontWeight: 600, color: "#4b5362", minWidth: "38px" }}>
			{(rate || 0).toFixed(1)}%
		</span>
	</div>
);

const AgentPerformanceTab = () => {
	const [loading, setLoading] = useState(false);
	const [perfData, setPerfData] = useState(null);
	const [filterMonth, setFilterMonth] = useState(() => {
		const d = new Date();
		return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}`;
	});
	const userRole = decodeRolesFromToken()[0];
	const isAdmin = [1, 9].includes(userRole);

	const fetchPerformance = useCallback(async () => {
		setLoading(true);
		try {
			const resp = await fetch(
				`${API_URL}/api/1.0.0/admin/referral/agent-performance?periodMonth=${filterMonth}`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						"X-JWT-Assertion": localStorage.getItem("token"),
					},
				}
			);
			if (resp.ok) {
				const json = await resp.json();
				setPerfData(json.data || null);
			}
		} catch (e) {
			console.error("Error fetching agent performance", e);
		} finally {
			setLoading(false);
		}
	}, [filterMonth]);

	useEffect(() => {
		fetchPerformance();
	}, [fetchPerformance]);

	const agents = perfData?.agents || [];
	const topPerformers = perfData?.topPerformers || [];

	// ---- Aggregates ----
	const totalOnboardings = agents.reduce((s, a) => s + (a.onboardingCount || 0), 0);
	const totalFirstTxns = agents.reduce((s, a) => s + (a.firstTxnCount || 0), 0);
	const totalCommissionCents = agents.reduce((s, a) => s + (a.totalCommissionCents || 0), 0);
	const totalPendingCents = agents.reduce((s, a) => s + (a.pendingCents || 0), 0);
	const totalApprovedCents = agents.reduce((s, a) => s + (a.approvedCents || 0), 0);
	const totalPaidCents = agents.reduce((s, a) => s + (a.paidCents || 0), 0);
	const avgConversion =
		agents.length > 0
			? agents.reduce((s, a) => s + (a.conversionRate || 0), 0) / agents.length
			: 0;

	// ---- Bar chart: onboardings vs first txns per agent ----
	const barChartData = {
		labels: agents.map((a) => a.agentName || `Agent #${a.agentUserId}`),
		datasets: [
			{
				label: "Onboardings",
				data: agents.map((a) => a.onboardingCount || 0),
				backgroundColor: "#3b82f6",
				borderRadius: 5,
				borderSkipped: false,
			},
			{
				label: "First Txns",
				data: agents.map((a) => a.firstTxnCount || 0),
				backgroundColor: "#10b981",
				borderRadius: 5,
				borderSkipped: false,
			},
		],
	};

	const barChartOptions = {
		responsive: true,
		plugins: {
			legend: { position: "top", labels: { font: { size: 12 }, boxWidth: 12 } },
			tooltip: { enabled: true },
		},
		scales: {
			y: {
				beginAtZero: true,
				ticks: { stepSize: 1, font: { size: 11 } },
				grid: { color: "#f3f4f6" },
			},
			x: { grid: { display: false }, ticks: { font: { size: 11 } } },
		},
	};

	// ---- Doughnut: commission status breakdown (admin only) ----
	const donutData = {
		labels: ["Pending", "Approved", "Paid"],
		datasets: [
			{
				data: [
					totalPendingCents / 100,
					totalApprovedCents / 100,
					totalPaidCents / 100,
				],
				backgroundColor: ["#f59e0b", "#10b981", "#2980B9"],
				borderWidth: 3,
				borderColor: "#ffffff",
				hoverOffset: 6,
			},
		],
	};

	const donutOptions = {
		responsive: true,
		plugins: {
			legend: { position: "bottom", labels: { font: { size: 12 }, boxWidth: 12 } },
			tooltip: {
				callbacks: {
					label: (ctx) => ` ZAR ${formatAmountValue(ctx.parsed)}`,
				},
			},
		},
		cutout: "65%",
	};

	// ---- Conversion rate bar chart (horizontal) ----
	const convChartData = {
		labels: agents.map((a) => a.agentName || `Agent #${a.agentUserId}`),
		datasets: [
			{
				label: "Conversion Rate (%)",
				data: agents.map((a) => (a.conversionRate || 0).toFixed(1)),
				backgroundColor: agents.map((_, i) =>
					["#6366f1", "#8b5cf6", "#a78bfa", "#c4b5fd"][i % 4]
				),
				borderRadius: 5,
				borderSkipped: false,
			},
		],
	};

	const convChartOptions = {
		indexAxis: "y",
		responsive: true,
		plugins: {
			legend: { display: false },
			tooltip: { callbacks: { label: (ctx) => ` ${ctx.parsed.x}%` } },
		},
		scales: {
			x: {
				beginAtZero: true,
				max: 100,
				ticks: { callback: (v) => `${v}%`, font: { size: 11 } },
				grid: { color: "#f3f4f6" },
			},
			y: { grid: { display: false }, ticks: { font: { size: 11 } } },
		},
	};

	const periodLabel = perfData?.periodMonth
		? formatYYYYMM(String(perfData.periodMonth))
		: "";

	return (
		<div>
			<FullPageLoader isActive={loading} />

			{/* ---- Filter Bar ---- */}
			<div className='referral-filter-bar'>
				<div className='filter-group'>
					<label>Period Month</label>
					<input
						type='month'
						value={
							filterMonth
								? `${filterMonth.substring(0, 4)}-${filterMonth.substring(4, 6)}`
								: ""
						}
						onChange={(e) => {
							if (e.target.value)
								setFilterMonth(e.target.value.replace("-", ""));
						}}
					/>
				</div>
				<ButtonLightTextIcon
					title='Refresh'
					icon={<CiRedo />}
					onClick={fetchPerformance}
				/>
			</div>

			{!loading && !perfData && (
				<div style={{ padding: "60px 20px", textAlign: "center", color: "#9ca3af" }}>
					<CiTrophy style={{ fontSize: "48px", marginBottom: "12px", opacity: 0.3 }} />
					<p style={{ fontSize: "14px" }}>No performance data for this period.</p>
				</div>
			)}

			{perfData && (
				<>
					{/* ---- KPI Summary Cards ---- */}
					<div
						className='referral-kpi-grid'
						style={{
							gridTemplateColumns: `repeat(${isAdmin ? 5 : 4}, 1fr)`,
							padding: "20px 20px 4px",
						}}>
						{[
							{
								label: "Total Agents",
								value: perfData.totalAgents ?? 0,
								color: "#475569",
								icon: <CiUser />,
							},
							{
								label: "Total Onboardings",
								value: totalOnboardings,
								color: "#3b82f6",
								icon: <CiUser />,
							},
							{
								label: "First Transactions",
								value: totalFirstTxns,
								color: "#10b981",
								icon: <CiCircleCheck />,
							},
							{
								label: "Avg. Conversion",
								value: `${avgConversion.toFixed(1)}%`,
								color: "#8b5cf6",
								icon: <CiStar />,
							},
							...(isAdmin
								? [
										{
											label: "Total Commission",
											value: `ZAR ${formatAmountValue(totalCommissionCents / 100)}`,
											color: "#7c3aed",
											icon: <CiTrophy />,
										},
								  ]
								: []),
						].map((card, i) => (
							<div className='referral-kpi-card' key={i}>
								<div
									className='kpi-icon'
									style={{
										color: card.color,
										backgroundColor: card.color + "18",
									}}>
									{card.icon}
								</div>
								<div className='kpi-content'>
									<p className='kpi-label'>{card.label}</p>
									<h4 className='kpi-value' style={{ color: card.color }}>
										{card.value}
									</h4>
								</div>
							</div>
						))}
					</div>

					{/* ---- Top 3 Performers Podium ---- */}
					{topPerformers.length > 0 && (
						<div className='agent-perf-section'>
							<div className='agent-perf-section__header'>
								<CiTrophy />
								<h6>Top Performers · {periodLabel}</h6>
							</div>
							<div className='agent-perf-podium'>
								{PODIUM_SLOTS.map((rank) => {
									const p = topPerformers.find((tp) => tp.rank === rank);
									if (!p) return <div key={rank} className='agent-perf-podium__slot' />;
									const meta = RANK_META[rank];
									const isFirst = rank === 1;
									return (
										<div
											key={p.agentUserId}
											className={`agent-perf-podium__card${isFirst ? " agent-perf-podium__card--first" : ""}`}
											style={{ boxShadow: meta.shadow }}>
											<div className='agent-perf-podium__emoji'>{meta.emoji}</div>
											<div
												className='agent-perf-podium__rank'
												style={{ color: meta.color }}>
												{meta.label} Place
											</div>
											<div className='agent-perf-podium__name'>
												{p.agentName || `Agent #${p.agentUserId}`}
											</div>
											<div className='agent-perf-podium__divider' />
											<div className='agent-perf-podium__stats'>
												<div className='agent-perf-podium__stat'>
													<span
														className='val'
														style={{ color: "#3b82f6" }}>
														{p.onboardingCount}
													</span>
													<span className='lbl'>Onboardings</span>
												</div>
												<div className='agent-perf-podium__stat'>
													<span
														className='val'
														style={{ color: "#10b981" }}>
														{p.firstTxnCount}
													</span>
													<span className='lbl'>First Txns</span>
												</div>
												<div className='agent-perf-podium__stat'>
													<span
														className='val'
														style={{ color: "#8b5cf6" }}>
														{(p.conversionRate || 0).toFixed(1)}%
													</span>
													<span className='lbl'>Conversion</span>
												</div>
												{isAdmin && (
													<div className='agent-perf-podium__stat agent-perf-podium__stat--commission'>
														<span
															className='val'
															style={{ color: "#7c3aed" }}>
															ZAR{" "}
															{formatAmountValue(
																(p.totalCommissionCents || 0) / 100
															)}
														</span>
														<span className='lbl'>Commission</span>
													</div>
												)}
											</div>
										</div>
									);
								})}
							</div>
						</div>
					)}

					{/* ---- Charts Row ---- */}
					<div className='referral-charts-row' style={{ padding: "0 20px 4px" }}>
						{/* Onboardings vs First Txns */}
						<div className='referral-chart-box' style={{ flex: 3 }}>
							<h6
								className='color-primary'
								style={{ marginBottom: "16px", fontSize: "13px", fontWeight: 600 }}>
								Onboardings vs First Transactions
							</h6>
							{agents.length > 0 ? (
								<Bar data={barChartData} options={barChartOptions} height={110} />
							) : (
								<p style={{ color: "#9ca3af", fontSize: "13px" }}>
									No data for this period.
								</p>
							)}
						</div>

						{/* Conversion Rate Horizontal Bar */}
						{agents.length > 0 && (
							<div className='referral-chart-box' style={{ flex: 2 }}>
								<h6
									className='color-primary'
									style={{
										marginBottom: "16px",
										fontSize: "13px",
										fontWeight: 600,
									}}>
									Conversion Rate by Agent
								</h6>
								<Bar
									data={convChartData}
									options={convChartOptions}
									height={agents.length > 4 ? agents.length * 28 : 110}
								/>
							</div>
						)}

						{/* Commission Doughnut — admin only */}
						{isAdmin && totalCommissionCents > 0 && (
							<div className='referral-chart-box referral-chart-box--sm'>
								<h6
									className='color-primary'
									style={{
										marginBottom: "16px",
										fontSize: "13px",
										fontWeight: 600,
									}}>
									Commission Status
								</h6>
								<Doughnut data={donutData} options={donutOptions} />
								<div className='agent-perf-donut-total'>
									<span className='agent-perf-donut-total__label'>Total</span>
									<span className='agent-perf-donut-total__value'>
										ZAR {formatAmountValue(totalCommissionCents / 100)}
									</span>
								</div>
							</div>
						)}
					</div>

					{/* ---- All Agents Table ---- */}
					<div className='agent-perf-section' style={{ paddingTop: 0 }}>
						<div className='agent-perf-section__header'>
							<CiGrid41 />
							<h6>All Agents · {periodLabel}</h6>
						</div>
						<div className='rs-table-holder' style={{ margin: "0 20px 24px" }}>
							<table className='rs-table'>
								<thead>
									<tr>
										<th>#</th>
										<th>Agent</th>
										<th>Onboardings</th>
										<th>First Txns</th>
										<th style={{ minWidth: "160px" }}>Conversion Rate</th>
										{isAdmin && (
											<>
												<th>Pending</th>
												<th>Approved</th>
												<th>Paid</th>
												<th>Total Commission</th>
											</>
										)}
									</tr>
								</thead>
								<tbody>
									{agents.length > 0 ? (
										agents.map((a, i) => (
											<tr key={a.agentUserId}>
												<td>{i + 1}</td>
												<td>
													<strong style={{ fontSize: "13px" }}>
														{a.agentName || `Agent #${a.agentUserId}`}
													</strong>
													<p style={{ fontSize: "11px", color: "#9ca3af" }}>
														ID: {a.agentUserId}
													</p>
												</td>
												<td>
													<span
														className='agent-perf-badge agent-perf-badge--blue'>
														{a.onboardingCount}
													</span>
												</td>
												<td>
													<span
														className='agent-perf-badge agent-perf-badge--green'>
														{a.firstTxnCount}
													</span>
												</td>
												<td>
													<ConversionBar rate={a.conversionRate} />
												</td>
												{isAdmin && (
													<>
														<td>
															<span style={{ color: "#f59e0b", fontWeight: 600, fontSize: "13px" }}>
																ZAR{" "}
																{formatAmountValue(
																	(a.pendingCents || 0) / 100
																)}
															</span>
														</td>
														<td>
															<span style={{ color: "#10b981", fontWeight: 600, fontSize: "13px" }}>
																ZAR{" "}
																{formatAmountValue(
																	(a.approvedCents || 0) / 100
																)}
															</span>
														</td>
														<td>
															<span style={{ color: "#2980B9", fontWeight: 600, fontSize: "13px" }}>
																ZAR{" "}
																{formatAmountValue(
																	(a.paidCents || 0) / 100
																)}
															</span>
														</td>
														<td>
															<span
																className='agent-perf-badge agent-perf-badge--purple'
																style={{ fontSize: "13px" }}>
																ZAR{" "}
																{formatAmountValue(
																	(a.totalCommissionCents || 0) / 100
																)}
															</span>
														</td>
													</>
												)}
											</tr>
										))
									) : (
										<tr>
											<td
												colSpan={isAdmin ? 9 : 5}
												style={{ textAlign: "center", padding: "24px" }}>
												<p>No agent data for this period.</p>
											</td>
										</tr>
									)}
								</tbody>
								{agents.length > 1 && isAdmin && (
									<tfoot>
										<tr className='agent-perf-table-total'>
											<td colSpan={2}>
												<strong>Total ({agents.length} agents)</strong>
											</td>
											<td>
												<strong style={{ color: "#3b82f6" }}>
													{totalOnboardings}
												</strong>
											</td>
											<td>
												<strong style={{ color: "#10b981" }}>
													{totalFirstTxns}
												</strong>
											</td>
											<td>
												<ConversionBar rate={avgConversion} />
											</td>
											<td>
												<strong style={{ color: "#f59e0b" }}>
													ZAR {formatAmountValue(totalPendingCents / 100)}
												</strong>
											</td>
											<td>
												<strong style={{ color: "#10b981" }}>
													ZAR {formatAmountValue(totalApprovedCents / 100)}
												</strong>
											</td>
											<td>
												<strong style={{ color: "#2980B9" }}>
													ZAR {formatAmountValue(totalPaidCents / 100)}
												</strong>
											</td>
											<td>
												<strong style={{ color: "#7c3aed", fontSize: "14px" }}>
													ZAR{" "}
													{formatAmountValue(totalCommissionCents / 100)}
												</strong>
											</td>
										</tr>
									</tfoot>
								)}
							</table>
						</div>
					</div>
				</>
			)}
		</div>
	);
};

// ====== Contests Tab ====== //

const ContestSchema = Yup.object().shape({
	contestName: Yup.string().required("Contest name is required"),
	periodMonth: Yup.string()
		.required("Period month required")
		.matches(/^\d{6}$/, "Must be in YYYYMM format"),
	metric: Yup.string().required("Metric is required"),
	rewardType: Yup.string().required("Reward type is required"),
	tier1Reward: Yup.number().min(0).required("1st place reward required"),
});

const ContestFormModal = ({ onClose, onSaved }) => {
	const { addToastMessage } = useUI();
	const [loading, setLoading] = useState(false);
	const [box] = useOutsideClick({ state: true, callBack: onClose });

	const initialValues = {
		contestName: "",
		periodMonth: "",
		metric: "FIRST_TXN_DONE",
		rewardType: "COMMISSION",
		tier1Reward: 0,
		tier2Reward: 0,
		tier3Reward: 0,
		eligibilityRoles: "AGENT",
	};

	const handleSubmit = async (values) => {
		setLoading(true);
		try {
			const resp = await fetch(`${API_URL}/api/1.0.0/admin/referral/contests`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-JWT-Assertion": localStorage.getItem("token"),
				},
				body: JSON.stringify(values),
			});
			if (resp.ok) {
				addToastMessage({ status: true, message: "Contest created successfully." });
				onSaved();
				onClose();
			} else {
				addToastMessage({ status: false, message: "Failed to create contest." });
			}
		} catch (e) {
			console.error(e);
			addToastMessage({ status: false, message: "An error occurred." });
		} finally {
			setLoading(false);
		}
	};

	return (
		<PopUpModal>
			<section id='rs-modal'>
				<div className='inner'>
					<div className='box' ref={box} style={{ width: "540px" }}>
						<div style={{ padding: "32px" }}>
							<h5 className='color-primary'>Create Monthly Contest</h5>
							<br />
							<Formik
								initialValues={initialValues}
								validationSchema={ContestSchema}
								onSubmit={handleSubmit}>
								{({ isSubmitting, setFieldValue, values }) => (
									<Form>
										<div className='referral-form-grid'>
											<div className='referral-form-field'>
												<label>Contest Name *</label>
												<Field name='contestName' placeholder='e.g. March Top Agents 2026' />
												<ErrorMessage name='contestName' component='p' className='error-messages' />
											</div>
											<div className='referral-form-field'>
												<label>Period Month *</label>
												<input
													type='month'
													onChange={(e) => {
														if (e.target.value) {
															setFieldValue("periodMonth", e.target.value.replace("-", ""));
														}
													}}
												/>
												<ErrorMessage name='periodMonth' component='p' className='error-messages' />
											</div>
											<div className='referral-form-field'>
												<label>Metric *</label>
												<Field as='select' name='metric'>
													<option value='REFERRALS_CAPTURED'>Referrals Captured</option>
													<option value='FIRST_TXN_DONE'>First Txn Done</option>
													<option value='REWARDED'>Rewarded</option>
												</Field>
											</div>
											<div className='referral-form-field'>
												<label>Reward Type *</label>
												<Field as='select' name='rewardType'>
													<option value='COMMISSION'>Bonus Commission</option>
													<option value='POINTS'>Bonus Points</option>
												</Field>
											</div>
											<div className='referral-form-field'>
												<label>1st Place Reward *</label>
												<Field name='tier1Reward' type='number' min='0' />
												<ErrorMessage name='tier1Reward' component='p' className='error-messages' />
											</div>
											<div className='referral-form-field'>
												<label>2nd Place Reward</label>
												<Field name='tier2Reward' type='number' min='0' />
											</div>
											<div className='referral-form-field'>
												<label>3rd Place Reward</label>
												<Field name='tier3Reward' type='number' min='0' />
											</div>
										</div>
										<div
											className='flex'
											style={{ marginTop: "24px", gap: "12px", justifyContent: "flex-end" }}>
											<ButtonLightTextIcon
												title='Cancel'
												icon={<CiCircleRemove />}
												type='button'
												onClick={onClose}
											/>
											<ButtonTextIcon
												title={loading ? "Creating..." : "Create Contest"}
												icon={<CiCircleCheck />}
												type='submit'
												disabled={isSubmitting || loading}
											/>
										</div>
									</Form>
								)}
							</Formik>
						</div>
					</div>
				</div>
			</section>
		</PopUpModal>
	);
};

const ContestsTab = () => {
	const [loading, setLoading] = useState(false);
	const [contests, setContests] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [pageSize] = useState(20);
	const [showForm, setShowForm] = useState(false);
	const userRole = decodeRolesFromToken()[0];

	const fetchContests = useCallback(async () => {
		setLoading(true);
		try {
			const params = buildQueryParams({ page, size: pageSize });
			const resp = await fetch(
				`${API_URL}/api/1.0.0/admin/referral/contests?${params}`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						"X-JWT-Assertion": localStorage.getItem("token"),
					},
				}
			);
			if (resp.ok) {
				const json = await resp.json();
				setContests(json.data?.items || json.data || []);
				setTotal(json.data?.total || 0);
			}
		} catch (e) {
			console.error("Error fetching contests", e);
		} finally {
			setLoading(false);
		}
	}, [page, pageSize]);

	useEffect(() => {
		fetchContests();
	}, [fetchContests]);

	const totalPages = Math.ceil(total / pageSize);
	const canManage = [1].includes(userRole);

	return (
		<div>
			<FullPageLoader isActive={loading} />

			<div className='referral-filter-bar'>
				<ButtonLightTextIcon
					title='Refresh'
					icon={<CiRedo />}
					onClick={fetchContests}
				/>
				{canManage && (
					<ButtonTextIcon
						title='Create Contest'
						icon={<CiTrophy />}
						onClick={() => setShowForm(true)}
					/>
				)}
			</div>

			<div className='rs-table-holder'>
				<table className='rs-table'>
					<thead>
						<tr>
							<th>#</th>
							<th>Contest Name</th>
							<th>Period</th>
							<th>Metric</th>
							<th>Reward Type</th>
							<th>1st Place</th>
							<th>2nd Place</th>
							<th>3rd Place</th>
							<th>Status</th>
							<th>Created At</th>
						</tr>
					</thead>
					<tbody>
						{contests.length > 0 ? (
							contests.map((c, i) => (
								<tr key={c.id || i}>
									<td>{page * pageSize + i + 1}</td>
									<td>
										<strong>{c.contestName}</strong>
									</td>
									<td>{formatYYYYMM(String(c.periodMonth))}</td>
									<td>{c.metric}</td>
									<td>{c.rewardType}</td>
									<td>{c.tier1Reward ?? "-"}</td>
									<td>{c.tier2Reward ?? "-"}</td>
									<td>{c.tier3Reward ?? "-"}</td>
									<td>
										<StatusBadge status={c.status || "DRAFT"} />
									</td>
									<td>{c.createdAt ? formatDateTime(c.createdAt) : "-"}</td>
								</tr>
							))
						) : (
							<tr>
								<td colSpan={10} style={{ textAlign: "center", padding: "20px" }}>
									<p>No contests found. Create your first monthly contest.</p>
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
			<SimplePagination
				page={page}
				totalPages={totalPages}
				onPageChange={setPage}
			/>

			{showForm && (
				<ContestFormModal
					onClose={() => setShowForm(false)}
					onSaved={fetchContests}
				/>
			)}
		</div>
	);
};

// ====== Referral Explorer Tab ====== //

const ReferralExplorerTab = () => {
	const [loading, setLoading] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [searchType, setSearchType] = useState("PHONE");
	const [result, setResult] = useState(null);
	const [searched, setSearched] = useState(false);

	const handleSearch = async () => {
		if (!searchTerm.trim()) return;
		setLoading(true);
		setResult(null);
		setSearched(true);
		try {
			const params = buildQueryParams({ search: searchTerm.trim(), searchType });
			const resp = await fetch(
				`${API_URL}/api/1.0.0/admin/referral/explorer?${params}`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						"X-JWT-Assertion": localStorage.getItem("token"),
					},
				}
			);
			if (resp.ok) {
				const json = await resp.json();
				setResult(json.data || null);
			}
		} catch (e) {
			console.error("Error in referral explorer", e);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className='referral-explorer'>
			<FullPageLoader isActive={loading} />

			<div className='referral-explorer-search'>
				<h6 className='color-primary'>Search Referral</h6>
				<p
					style={{ fontSize: "13px", color: "#8f8f8f", marginTop: "4px", marginBottom: "16px" }}>
					Search by customer phone, email, or referral code to view the complete
					referral chain.
				</p>
				<div className='flex center-left' style={{ gap: "12px" }}>
					<select
						value={searchType}
						onChange={(e) => setSearchType(e.target.value)}
						style={{ width: "180px" }}>
						<option value='PHONE'>Phone / Email</option>
						<option value='REFERRAL_CODE'>Referral Code</option>
						<option value='REFERRER_ID'>Referrer User ID</option>
					</select>
					<input
						type='text'
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						placeholder={
							searchType === "PHONE"
								? "Enter phone or email..."
								: searchType === "REFERRAL_CODE"
								? "Enter referral code..."
								: "Enter referrer user ID..."
						}
						onKeyDown={(e) => e.key === "Enter" && handleSearch()}
						style={{ flex: 1, maxWidth: "400px" }}
					/>
					<ButtonTextIcon
						title='Search'
						icon={<CiSearch />}
						onClick={handleSearch}
					/>
				</div>
			</div>

			{searched && !loading && result && (
				<div className='referral-explorer-result'>
					{/* Referrer Info */}
					<div className='referral-explorer-referrer'>
						<h6 className='color-primary'>Referrer</h6>
						<div className='referral-user-card'>
							<div className='referral-user-avatar'>
								<CiUser />
							</div>
							<div>
								<p className='f-w-600' style={{ fontSize: "15px" }}>
									{result.referrer?.fullName || result.referrer?.email || "-"}
								</p>
								<p style={{ fontSize: "12px", color: "#8f8f8f" }}>
									{result.referrer?.email}
								</p>
								<p style={{ fontSize: "12px", color: "#8f8f8f" }}>
									Phone: {result.referrer?.phone || "-"}
								</p>
								<div style={{ marginTop: "8px" }}>
									<span className='referral-code-badge'>
										Code: {result.referrer?.referralCode || "-"}
									</span>
								</div>
							</div>
						</div>
					</div>

					{/* Referred Users Table */}
					{result.referredUsers && result.referredUsers.length > 0 && (
						<div style={{ marginTop: "24px" }}>
							<h6 className='color-primary' style={{ marginBottom: "12px" }}>
								Referred Users ({result.referredUsers.length})
							</h6>
							<div className='rs-table-holder' style={{ padding: "0" }}>
								<table className='rs-table'>
									<thead>
										<tr>
											<th>#</th>
											<th>Name / Email</th>
											<th>Registration Date</th>
											<th>Referral Status</th>
											<th>First Txn Date</th>
											<th>First Txn Amount</th>
											<th>Reward Status</th>
										</tr>
									</thead>
									<tbody>
										{result.referredUsers.map((u, i) => (
											<tr key={u.userId || i}>
												<td>{i + 1}</td>
												<td>
													<div>
														<strong style={{ fontSize: "13px" }}>
															{u.fullName || "-"}
														</strong>
														<p style={{ fontSize: "11px", color: "#8f8f8f" }}>
															{u.email}
														</p>
													</div>
												</td>
												<td>
													{u.registeredAt ? formatDateTime(u.registeredAt) : "-"}
												</td>
												<td>
													<StatusBadge status={u.referralStatus || "NONE"} />
												</td>
												<td>
													{u.firstTransactionAt
														? formatDateTime(u.firstTransactionAt)
														: "-"}
												</td>
												<td>
													{u.firstTransactionAmount
														? `ZAR ${formatAmountValue(u.firstTransactionAmount)}`
														: "-"}
												</td>
												<td>
													{u.rewardStatus ? (
														<StatusBadge status={u.rewardStatus} />
													) : (
														"-"
													)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					)}
				</div>
			)}

			{searched && !loading && !result && (
				<div
					className='referral-explorer-empty'
					style={{ padding: "40px 20px", textAlign: "center" }}>
					<CiSearch style={{ fontSize: "48px", color: "#d1d5db", display: "block", margin: "0 auto 16px" }} />
					<p style={{ color: "#8f8f8f" }}>
						No referral data found for the search criteria.
					</p>
				</div>
			)}
		</div>
	);
};

// ====== Main Referral Program Page ====== //

const ReferralProgram = () => {
	const { setSegmentTitle } = useUI();

	useEffect(() => {
		setSegmentTitle("Referral Program");
	}, []);

	const tabs = [
		{
			id: "overview",
			title: "Overview",
			content: <OverviewTab />,
		},
		{
			id: "rules",
			title: "Referral Rules",
			content: <RulesTab />,
		},
		{
			id: "rewards",
			title: "Rewards Ledger",
			content: <RewardsLedgerTab />,
		},
		{
			id: "commissions",
			title: "Agent Commission",
			content: <AgentCommissionTab />,
		},
		{
			id: "performance",
			title: "Agent Performance",
			content: <AgentPerformanceTab />,
		},
		{
			id: "contests",
			title: "Contests",
			content: <ContestsTab />,
		},
		{
			id: "explorer",
			title: "Explorer",
			content: <ReferralExplorerTab />,
		},
	];

	return (
		<>
			<PageTitle title='Referral Program' />
			<section>
				<div className='referral-program-header'>
					{/* <h4 className='color-primary'>Referral Program</h4>
					<p className='color-faded' style={{ fontSize: "13px" }}>
						Manage referral rules, track rewards, and monitor agent commissions.
					</p> */}
				</div>
				<RSTabs options={tabs} />
			</section>
		</>
	);
};

export default ReferralProgram;
